import { Link } from "react-router-dom";
import { Phone, MapPin, Facebook, Instagram, ArrowLeft, ExternalLink } from "lucide-react";
import { useContactSettings } from "@/hooks/useContactSettings";

const linkGroups = [
  {
    title: "أقسام الموقع",
    links: [
      { label: "الرئيسية", path: "/" },
      { label: "من نحن", path: "/من-نحن" },
      { label: "معرض الأعمال", path: "/معرض-الأعمال" },
      { label: "العروض والخصومات", path: "/العروض" },
      { label: "المدونة", path: "/المدونة" },
    ],
  },
  {
    title: "الخدمات",
    links: [
      { label: "خدماتنا", path: "/خدماتنا" },
      { label: "طلب تصميم خاص", path: "/طلب-تصميم" },
      { label: "مقارنة المنتجات", path: "/مقارنة" },
      { label: "تقدير التكلفة", path: "/خدماتنا" },
    ],
  },
  {
    title: "الطلبات والحجز",
    links: [
      { label: "حجز موعد", path: "/حجز-موعد" },
      { label: "طلب طوارئ", path: "/طوارئ" },
      { label: "تتبع الطلب", path: "/تتبع-الطلب" },
      { label: "برنامج الولاء", path: "/برنامج-الولاء" },
      { label: "برنامج الإحالة", path: "/الإحالة" },
    ],
  },
  {
    title: "أدوات ذكية",
    links: [
      { label: "تصميم ذكي AI", path: "/تصميم-ذكي" },
      { label: "خريطة المشاريع", path: "/خريطة-المشاريع" },
      { label: "تواصل معنا", path: "/تواصل-معنا" },
    ],
  },
];

export default function Footer() {
  const { settings, getMainPhone, getWhatsAppLink } = useContactSettings();
  const mainPhone = getMainPhone();

  const phoneNumbers = [
    { label: "النجارة", nums: [settings.phone_carpentry_1, settings.phone_carpentry_2].filter(Boolean) },
    { label: "الألمنيوم", nums: [settings.phone_aluminum_1, settings.phone_aluminum_2].filter(Boolean) },
    { label: "الحديد", nums: [settings.phone_iron_1, settings.phone_iron_2].filter(Boolean) },
    { label: "المدير العام", nums: [settings.phone_manager].filter(Boolean) },
  ].filter(g => g.nums.length > 0);

  return (
    <footer className="bg-card border-t-2 border-primary/20">
      <div className="container mx-auto py-12 md:py-16">
        {/* Main grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 mb-10">
          {/* Brand */}
          <div className="col-span-2">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-woody flex items-center justify-center shadow-sm">
                <span className="text-primary-foreground font-bold text-lg">ش</span>
              </div>
              <div>
                <span className="font-bold text-lg text-foreground block leading-tight">مجمع الشويع الصناعي</span>
                <span className="text-[11px] text-primary font-medium">الموقع الرسمي والوحيد</span>
              </div>
            </div>
            <p className="text-muted-foreground text-sm leading-relaxed mb-5">
              متخصصون في جميع أعمال النجارة والألمنيوم والحديد بأعلى جودة وأفضل الأسعار في صعدة، اليمن.
            </p>

            {/* Contact numbers */}
            <div className="space-y-2">
              {phoneNumbers.map(group => (
                <div key={group.label}>
                  <span className="text-[11px] text-muted-foreground font-semibold uppercase tracking-wider">{group.label}</span>
                  {group.nums.map(num => (
                    <a key={num} href={`tel:+967${num}`} className="flex items-center gap-2 text-foreground/80 hover:text-primary text-sm transition-colors group">
                      <Phone className="h-3 w-3 text-primary/60 group-hover:text-primary" />
                      <span dir="ltr" className="font-medium">+967 {num}</span>
                    </a>
                  ))}
                </div>
              ))}
              <div className="flex items-center gap-2 text-foreground/80 text-sm mt-2">
                <MapPin className="h-3.5 w-3.5 text-primary/60" />
                <span>حيدان، صعدة، اليمن</span>
              </div>
            </div>

            {/* Social */}
            <div className="flex items-center gap-2 mt-4">
              <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center text-primary hover:bg-primary hover:text-primary-foreground transition-all" aria-label="واتساب">
                <Phone className="h-4 w-4" />
              </a>
              {settings.facebook_link && (
                <a href={settings.facebook_link} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center text-primary hover:bg-primary hover:text-primary-foreground transition-all" aria-label="فيسبوك">
                  <Facebook className="h-4 w-4" />
                </a>
              )}
              {settings.instagram_link && (
                <a href={settings.instagram_link} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center text-primary hover:bg-primary hover:text-primary-foreground transition-all" aria-label="إنستغرام">
                  <Instagram className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>

          {/* Link groups */}
          {linkGroups.map((group) => (
            <div key={group.title}>
              <h3 className="font-bold text-foreground mb-4 text-sm">{group.title}</h3>
              <div className="flex flex-col gap-2.5">
                {group.links.map((link) => (
                  <Link
                    key={link.path + link.label}
                    to={link.path}
                    className="text-muted-foreground hover:text-primary text-sm transition-all flex items-center gap-1.5 group hover:translate-x-[-4px]"
                  >
                    <ArrowLeft className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity text-primary" />
                    <span className="group-hover:underline underline-offset-2">{link.label}</span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Copyright */}
        <div className="border-t border-border pt-6 flex flex-col items-center gap-3">
          <p className="text-foreground text-sm font-medium text-center">
            جميع الحقوق محفوظة لدى <strong>عبدالله طيب الشويع</strong> — هاتف: <a href="tel:+967777196039" className="text-primary hover:underline" dir="ltr">777196039</a>
          </p>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <Link to="/سياسة-الخصوصية" className="hover:text-primary transition-colors hover:underline underline-offset-2">سياسة الخصوصية</Link>
            <span>•</span>
            <Link to="/شروط-الاستخدام" className="hover:text-primary transition-colors hover:underline underline-offset-2">شروط الاستخدام</Link>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>✨</span>
            <span>تم بناء وتطوير الموقع بواسطة <strong className="text-foreground">عبدالله الشويع</strong> — <a href="tel:+96777196039" className="text-primary hover:underline" dir="ltr">77196039</a></span>
          </div>
        </div>
      </div>
    </footer>
  );
}
